import { ICliJob } from 'cli/types';
import { mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse } from 'core/types';
import {
  getQueryString,
  getQueryStringToSnakeCase,
  objectKeysCamelToSnakeCase,
  objectKeysSnakeCaseToCamelCase,
} from 'core/utils';
import {
  IPart,
  IPartAutocomplete,
  IPartAutocompleteRequestPayload,
  IPartDetails,
  IPartHistoryItem,
  IPartHistoryRequestPayload,
  IPartInventoryItem,
  IPartInventoryPayload,
  IPartManufacturer,
  IPartMedia,
  IPartMediaActionPayload,
  IPartMediaRequestPayload,
  IPartRequestPayload,
  IPartsSearchRequestPayload,
  IPartVideoPayload,
  ISalesHistoryItem,
  ISalesHistoryRequestPayload,
  ISavePartPayload,
  ISavePartResponse,
} from 'parts/types';

export const getManyAutocomplete = async (
  requestPayload?: IPartAutocompleteRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartAutocomplete>> => {
  const { data } = await partfinitiClient.get(
    `/parts/index/autocomplete?${getQueryString(objectKeysCamelToSnakeCase(requestPayload))}`,
    signal
  );

  return mapRecordsFromServer<IPartAutocomplete>(data.parts, data.total_count);
};

export const getManySearch = async (
  requestPayload?: IPartsSearchRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPart>> => {
  const { data } = await partfinitiClient.get(
    `/search/part?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IPart>(data.data, data.recordsFiltered, data.totals);
};

export const getPartInventory = async (
  requestPayload: IPartInventoryPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartInventoryItem>> => {
  const { data } = await partfinitiClient.get(
    `/inventory/item/items?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IPartInventoryItem>(data?.data, data?.recordsFiltered);
};

export const getPart = async (
  requestPayload: IPartRequestPayload,
  signal?: AbortSignal
): Promise<{ part: IPartDetails | null; manufacturer: IPartManufacturer | null }> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/index/get-details?${getQueryString(objectKeysCamelToSnakeCase(requestPayload))}`,
    signal
  );

  return {
    part: data?.part ? (objectKeysSnakeCaseToCamelCase(data.part) as IPartDetails) : null,
    manufacturer: data?.manufacturer
      ? (objectKeysSnakeCaseToCamelCase(data.manufacturer) as IPartManufacturer)
      : null,
  };
};

export const exportDatasheets = async (data): Promise<ICliJob> =>
  (await partfinitiClient.postForm('/part_v2/media/generate-datasheet-archive', data)).cliJob;

export const getPartMedia = async (
  requestPayload: IPartMediaRequestPayload,
  signal?: AbortSignal
): Promise<IPartMedia> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/media/data?${getQueryString(objectKeysCamelToSnakeCase(requestPayload))}`,
    signal
  );

  const camelCased = objectKeysSnakeCaseToCamelCase(data);

  // BE returns `documents_data` as an object keyed by file id; normalize to array.
  const toArray = <T>(value): T[] => {
    if (Array.isArray(value)) {
      return value;
    }

    return value && typeof value === 'object' ? Object.values(value) : [];
  };

  return {
    imagesData: toArray(camelCased?.imagesData),
    documentsData: toArray(camelCased?.documentsData),
    videosData: toArray(camelCased?.videosData),
  };
};

export const setDefaultPartImage = async (payload: IPartMediaActionPayload) =>
  partfinitiClient.postForm('/parts/media/set-main-image', payload);

export const setDefaultPartDatasheet = async (payload: IPartMediaActionPayload) =>
  partfinitiClient.postForm('/part_v2/index/set-default-datasheet', payload);

export const deletePartMediaFile = async (payload: IPartMediaActionPayload) =>
  partfinitiClient.postForm('/parts/media/delete-image', payload);

export const deletePartVideo = async (payload: { partVideo: { id: number } }) =>
  partfinitiClient.postForm('/part_v2/media/delete-video', payload);

export const savePartVideo = async (payload: IPartVideoPayload) =>
  partfinitiClient.postForm('/parts/media/save-video', payload);

export const upsertUnverifiedPart = async (data) =>
  partfinitiClient.postForm('/part_v2/index/save-unverified', data, {
    includeNullValues: true,
  });

export const savePart = async (payload: ISavePartPayload): Promise<ISavePartResponse> => {
  const response: ISavePartResponse = await partfinitiClient.postForm('/parts/edit/save', payload);

  // BE returns `errors: [{ field, message }]` on validation failure. `useHandleErrors` would
  // render the array as JSX children and crash; flatten to a single `error` string instead.
  if (!response?.success && Array.isArray(response?.errors) && response.errors.length > 0) {
    return {
      ...response,
      error: response.errors.map(({ field, message }) => `${field}: ${message}`).join(' • '),
    };
  }

  return response;
};

export const getPartHistory = async (
  requestPayload: IPartHistoryRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartHistoryItem>> => {
  const { data } = await partfinitiClient.get(
    `/parts/quote-history/quote-history-items?${getQueryString(
      objectKeysCamelToSnakeCase(requestPayload)
    )}`,
    signal
  );

  return mapRecordsFromServer<IPartHistoryItem>(data?.data, data?.recordsFiltered);
};

export const getSalesHistory = async (
  requestPayload?: ISalesHistoryRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<ISalesHistoryItem>> => {
  const { data } = await partfinitiClient.get(
    `/external/sales-order-history/items?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<ISalesHistoryItem>(
    data?.records,
    undefined,
    undefined,
    undefined,
    data?.hasMore
  );
};
